import React from 'react';
import { Controller, useWatch } from 'react-hook-form';
import type {
  Control,
  UseFormRegister,
  FieldErrors,
  UseFormSetValue,
} from 'react-hook-form';
import { VirtualizedFormSelect, Alert } from '@roger/ui-components';
import { AlertCircle } from 'lucide-react';
import { DateField } from '../../components/FormFields';
import type { SchedulingStepFormData } from '../../schemas/schedulingStepFormSchema';
import type { KinnserClinicDataResponse } from '../../services/api/types.gen';

interface RogerSchedulingStepFormContentProps {
  // Form controls
  control: Control<SchedulingStepFormData>;
  register: UseFormRegister<SchedulingStepFormData>;
  errors: FieldErrors<SchedulingStepFormData>;
  setValue: UseFormSetValue<SchedulingStepFormData>;
  handleFieldBlur: (fieldEvent: React.FocusEvent<HTMLElement>) => void;

  // Save status
  saveToServer: (data: Partial<SchedulingStepFormData>) => void;

  // Clinics
  clinicData: KinnserClinicDataResponse | null;
  loadingClinicData: boolean;
}

export const RogerSchedulingStepFormContent: React.FC<
  RogerSchedulingStepFormContentProps
> = ({
  control,
  register,
  errors,
  setValue,
  handleFieldBlur,
  saveToServer,
  clinicData,
  loadingClinicData,
}) => {
  // Watch episode start date to calculate episode end date
  const episodeStartDate = useWatch({
    control,
    name: 'kinnser_episode_start_date',
  });

  return (
    <div className='flex flex-col'>
      {/* Validation errors */}
      {Object.keys(errors).length > 0 && (
        <Alert className='mb-3 py-2 px-3 bg-red-50 border-red-100 text-red-900 text-xs'>
          <div className='flex items-start gap-2'>
            <AlertCircle className='h-4 w-4 mt-0.5 flex-shrink-0 text-red-600' />
            <div className='flex-1'>
              <p className='font-medium mb-1'>
                Missing fields required to schedule:
              </p>
              <ul
                className='flex flex-wrap gap-1'
                role='list'
                aria-label='Validation errors'
              >
                {Object.entries(errors)
                  .map(([field, error]) => {
                    const message =
                      (error as { message?: string })?.message || '';
                    const cleanMessage = message.replace(/ is required$/, '');

                    return { field, cleanMessage };
                  })
                  .filter(({ cleanMessage }) => cleanMessage !== '')
                  .map(({ field, cleanMessage }, i, arr) => (
                    <li key={field} role='listitem' className='text-xs'>
                      {cleanMessage}
                      {i < arr.length - 1 ? ',' : ''}
                    </li>
                  ))}
              </ul>
            </div>
          </div>
        </Alert>
      )}

      <form className='space-y-5' onSubmit={e => e.preventDefault()}>
        {/* Schedule Start of Care */}
        <div className='grid grid-cols-3 gap-3'>
          {/* Row 1: Clinician, Case Manager, SOC Type */}
          <VirtualizedFormSelect
            label='Clinician'
            placeholder='Select clinician...'
            options={clinicData?.clinicians || []}
            required={true}
            disabled={loadingClinicData}
            error={errors.kinnser_clinician_id?.message}
            {...register('kinnser_clinician_id', {
              onBlur: handleFieldBlur,
            })}
          />

          <VirtualizedFormSelect
            label='Case Manager'
            placeholder='Select case manager...'
            options={clinicData?.case_managers || []}
            required={true}
            disabled={loadingClinicData}
            error={errors.kinnser_case_manager_id?.message}
            {...register('kinnser_case_manager_id', {
              onBlur: handleFieldBlur,
            })}
          />

          <VirtualizedFormSelect
            label='Type of SOC'
            placeholder='Select SOC type...'
            options={[
              { value: 'SN', label: 'Nursing' },
              { value: 'PT', label: 'Physical Therapy' },
              { value: 'ST', label: 'Speech Therapy' },
              { value: 'OT', label: 'Occupational Therapy' },
            ]}
            required={true}
            disabled={loadingClinicData}
            error={errors.roger_type_of_soc?.message}
            {...register('roger_type_of_soc', {
              onBlur: handleFieldBlur,
            })}
          />
        </div>

        {/* Row 2: Dates */}
        <div className='grid grid-cols-3 gap-3'>
          <Controller
            name='kinnser_soc_date'
            control={control}
            render={({ field: { value, onChange, onBlur } }) => (
              <DateField
                label='Start of Care Date'
                value={value}
                htmlFor='rogerSocDate'
                required={true}
                disabled={false}
                onChange={val => {
                  onChange(val);
                  const toSave: Partial<SchedulingStepFormData> = {
                    kinnser_soc_date: val,
                  };
                  // Copy to episode start date if it's empty
                  if (!episodeStartDate && val) {
                    setValue('kinnser_episode_start_date', val);
                    toSave.kinnser_episode_start_date = val;
                  }
                  saveToServer(toSave);
                }}
                onBlur={onBlur}
                yearRange={{
                  fromYear: new Date().getFullYear() - 1,
                  toYear: new Date().getFullYear() + 1,
                }}
              />
            )}
          />

          <Controller
            name='kinnser_episode_start_date'
            control={control}
            render={({ field: { value, onChange, onBlur } }) => (
              <DateField
                label='Episode Start Date'
                value={value}
                htmlFor='rogerEpisodeStartDate'
                required={true}
                disabled={false}
                onChange={val => {
                  onChange(val);
                  const toSave: Partial<SchedulingStepFormData> = {
                    kinnser_episode_start_date: val,
                  };

                  saveToServer(toSave);
                }}
                onBlur={onBlur}
                yearRange={{
                  fromYear: new Date().getFullYear() - 1,
                  toYear: new Date().getFullYear() + 1,
                }}
              />
            )}
          />
        </div>
      </form>
    </div>
  );
};
